import {
  Alert,
  Box,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { resumeBusinessCompanyRelation } from 'lib/api/enrollment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getBusinessRelationsByStatus } from '../../../../lib/api';
import FilterComponent from '../../../../lib/common/FilterNonSearch';
import CustomButton from '../../../../lib/designSystem/Button';
import CenteredCircularProgress from '../../../../lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from '../../../../lib/designSystem/HeaderWithActions';
import EnhancedTable from '../../../../lib/designSystem/Table';
import { showAlert } from '../../../../lib/functions/utils';
import { validatePermissions } from '../../../../lib/functions/utils';
import { AuthState } from '../../../../services/redux/authSlice';
import {
  BusinessRelation,
  RelationStatus,
} from '../../types/businessRelations';
import EmptyState from './EmptyState';

interface StatusRelationsTabProps {
  status: RelationStatus;
  title: string;
  emptyStateType: 'suspended' | 'deleted';
  showDaysUntilMonthEnd?: boolean;
}

const StatusRelationsTab: React.FC<StatusRelationsTabProps> = ({
  status,
  title,
  emptyStateType,
  showDaysUntilMonthEnd = false,
}) => {
  const [relations, setRelations] = useState<BusinessRelation[]>([]);
  const [filteredRelations, setFilteredRelations] = useState<
    BusinessRelation[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [openReactivateDialog, setOpenReactivateDialog] = useState(false);
  const [selectedBusiness, setSelectedBusiness] =
    useState<BusinessRelation | null>(null);
  const [checkBoxConfirm, setCheckBoxConfirm] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);

  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);

  // Headers de la tabla
  const headers = [
    { key: 'businessName', label: 'Nombre' },
    { key: 'identifier', label: 'Rut' },
    { key: 'statusLabel', label: 'Estado SII', type: 'sii-connection' },
    { key: 'createdAt', label: 'Creado', type: 'date-large' },
    {
      key: 'pausedAt',
      label:
        status === RelationStatus.SUSPENDED ? 'Suspendido en' : 'Eliminado en',
      type: 'date-large',
    },
  ];

  if (validatePermissions(user, 'businesses_read_all')) {
    headers.push({ key: 'users', label: 'Ejecutivos' });
  }

  const actions = [
    {
      action: (id: string) => handleReactivateBusiness(id),
      actionLabel: 'Reactivar',
      canShow: () => canReactivateBusiness(),
    },
  ];

  const canReactivateBusiness = () => {
    return validatePermissions(user, 'businesses_reactivate');
  };

  const handleReactivateBusiness = (id: string) => {
    const business = relations.find((r) => r.id === id);
    if (!business) return;

    setSelectedBusiness(business);
    setOpenReactivateDialog(true);
  };

  const confirmReactivate = async () => {
    if (!selectedBusiness) return;

    setIsReactivating(true);
    try {
      await resumeBusinessCompanyRelation(dispatch, {
        businessIdentifier: selectedBusiness.identifier,
      });

      setOpenReactivateDialog(false);
      setCheckBoxConfirm(false);
      setSelectedBusiness(null);

      await fetchRelations();
    } catch (error) {
      console.error('Error al reactivar la empresa:', error);
      showAlert(dispatch, 'error', 'Error al reactivar la empresa');
    } finally {
      setIsReactivating(false);
    }
  };

  const fetchRelations = async () => {
    setLoading(true);
    try {
      const response = await getBusinessRelationsByStatus(dispatch, status);
      const relationsData = response?.relations || [];

      setRelations(relationsData);
      setFilteredRelations(relationsData);
    } catch (error) {
      showAlert(
        dispatch,
        'error',
        `Error al obtener las ${title.toLowerCase()}`,
      );
    }
    setLoading(false);
  };

  useEffect(() => {
    void fetchRelations();
  }, [status]);

  // Botones del header
  const buttonsData: CustomButtonProps[] = [
    {
      name: 'Filtro',
      action: () => {},
      color: 'primary',
      customButton: (
        <FilterComponent
          data={relations}
          filterFields={[
            { key: 'businessName', filterName: 'Nombre' },
            { key: 'identifier', filterName: 'Rut' },
          ]}
          setFilteredData={setFilteredRelations}
        />
      ),
    },
  ];

  const getDaysUntilMonthEnd = () => {
    const now = new Date();
    const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const diffTime = lastDayOfMonth.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysUntilMonthEnd = getDaysUntilMonthEnd();

  if (loading) {
    return <CenteredCircularProgress />;
  }

  if (relations.length === 0) {
    return <EmptyState type={emptyStateType} />;
  }

  return (
    <Box>
      {showDaysUntilMonthEnd && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Importante:</strong> Las cuentas con{' '}
            <strong>cancelación efectiva al cierre de ciclo</strong> mantendrán
            el acceso al servicio hasta fin de mes y serán eliminadas
            automáticamente en <strong>{daysUntilMonthEnd} días</strong>.
          </Typography>
        </Alert>
      )}

      <HeaderWithActions
        type="subHeader"
        title={title}
        variant="body1"
        buttons={buttonsData}
      />

      {/* Información adicional */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Chip
          label={
            status === RelationStatus.SUSPENDED
              ? `Por eliminar ${relations.length} empresa(s)`
              : `${relations.length} empresa(s) eliminada(s)`
          }
          color={status === RelationStatus.SUSPENDED ? 'warning' : 'error'}
          variant="outlined"
          size="small"
        />
        {showDaysUntilMonthEnd && (
          <Chip
            label={`Eliminación en ${daysUntilMonthEnd} días`}
            color="error"
            variant="outlined"
            size="small"
          />
        )}
      </Box>

      {/* Tabla de relaciones */}
      <EnhancedTable
        dataHeaders={headers}
        data={filteredRelations}
        showActions
        rowsPerPageDefault={50}
        actions={actions}
        exportToFile="xlsx"
        lastElement
      />

      {/* Dialog de confirmación para reactivar */}
      <Dialog
        open={openReactivateDialog}
        onClose={() => {
          setOpenReactivateDialog(false);
          setCheckBoxConfirm(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Reactivar cuenta{' '}
          {status === RelationStatus.SUSPENDED ? 'suspendida' : 'eliminada'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Typography variant="body1" gutterBottom>
              ¿Estás seguro de que quieres reactivar la empresa{' '}
              <strong>{selectedBusiness?.businessName}</strong>?
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Al reactivar esta cuenta{' '}
              {status === RelationStatus.SUSPENDED ? 'suspendida' : 'eliminada'}
              , se cobrará el mes completo.
            </Typography>

            <Box display="flex" alignItems="center" mb={2}>
              <Checkbox
                checked={checkBoxConfirm}
                onChange={(e) => setCheckBoxConfirm(e.target.checked)}
                id={`confirm-reactivate-${status.toLowerCase()}-checkbox`}
              />
              <Typography
                variant="body2"
                component="label"
                htmlFor={`confirm-reactivate-${status.toLowerCase()}-checkbox`}
              >
                Confirmo que estoy de acuerdo con el cobro del mes completo.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <CustomButton
            onClick={() => {
              setOpenReactivateDialog(false);
              setCheckBoxConfirm(false);
            }}
            color="secondary"
          >
            Cancelar
          </CustomButton>
          <CustomButton
            onClick={confirmReactivate}
            color="primary"
            disabled={!checkBoxConfirm || isReactivating}
            loading={isReactivating}
          >
            {isReactivating ? 'Reactivando...' : 'Reactivar cuenta'}
          </CustomButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StatusRelationsTab;
